import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { Box, HStack, VStack, Text } from "@chakra-ui/layout";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Image,
  Divider,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Link, useSearchParams } from "react-router-dom";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { CopyText } from "../../components/Utilities/CopyText";
import { OdmaUrlType } from "../../enums/OdmaUrlType";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { useToast } from "../../hooks/useToast";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { RedemptionForm } from "../../forms/RedemptionDetailForm";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

interface RedemptionDetailProps {
  create?: boolean;
}

export const RedemptionDetail = (props: RedemptionDetailProps) => {
  const [searchParams] = useSearchParams();
  const { businessId, redemptionId } = useParams();
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );
  const [redemption, setRedemption] =
    useState<CherryHubAdminApi.RedemptionFormConfigurationItem | null>();
  const apiClient = useAdminApiClient();

  if (!businessId) {
    return <ErrorMessage>Business ID is required</ErrorMessage>;
  }

  const {
    data: redemptionDoc,
    isLoading,
    error: redemptionError,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !redemptionId) {
        return null;
      }
      return apiClient.getRedemptionFormConfiguration(businessId, redemptionId);
    },
    [businessId, redemptionId]
  );

  // Get digital membership redemption form detail
  useEffect(() => {
    setRedemption(redemptionDoc);
  }, [redemptionDoc]);

  if (redemptionError) return <ErrorMessage>{redemptionError}</ErrorMessage>;

  const { data: odmaUrl, isLoading: isOdmaUrlLoading } = useAdminApiRequest(
    (adminApiClient) => {
      if (!businessId || !redemptionId) {
        return null;
      }
      return adminApiClient.getOdmaUrl(
        businessId,
        OdmaUrlType.DigitalMembershipRedemption,
        redemptionId!
      );
    },
    [businessId, redemptionId]
  );

  const onSuccess = useCallback(
    (redemption: CherryHubAdminApi.RedemptionFormConfigurationItem) => {
      setRedemption(redemption);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Redemption created.");
        navigate(
          `/admin/businesses/${businessId}/redemptions/${redemption.Meta?.Id}`
        );
      } else {
        showSuccessToast("Redemption updated.");
      }
    },
    [showSuccessToast, redemption]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Redemption update failed.");
    },
    [showErrorToast]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Digital Membership Redemption Form
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/redemptions`}
              >
                Digital Membership Redemption Forms
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/redemptions/${redemptionId}/${
                  props.create ? "create" : redemption?.id
                }`}
              >
                {props.create
                  ? "New"
                  : redemption?.FriendlyName ?? redemption?.Meta?.Id}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || isOdmaUrlLoading || !redemption) && !props.create && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="flex-start" spacing={4}>
                <Box flexGrow={1}>
                  <Skeleton w="320px" height="40px" />
                </Box>
                {!editMode && <Skeleton w="108px" height="40px" />}
                <Skeleton w="92px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <VStack w="100%" spacing={6} alignItems="flex-start">
                <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
                <Skeleton w="100%" height="40px" />
                <SkeletonText w="100%" mt="4" noOfLines={5} spacing="4" />
                <Skeleton w="100%" height="40px" />
              </VStack>
            </VStack>
          )}

          {!isLoading && !isOdmaUrlLoading && editMode && (
            <RedemptionForm
              businessId={businessId!}
              redemption={redemption!}
              onCancel={() =>
                props.create
                  ? navigate(`/admin/businesses/${businessId}/redemptions`)
                  : setEditMode(false)
              }
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}

          {!isLoading && !isOdmaUrlLoading && !editMode && redemption && (
            <>
              <HStack w="100%" spacing={2}>
                <Box flexGrow={2} pb={4}>
                  <CopyText text={redemption?.Meta?.Id!} hover adjustMargin>
                    <Text fontSize="md" color="cherryUi.600">
                      {redemption?.id}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{redemption?.FriendlyName}</b>
                  </Text>
                </Box>

                <Box>
                  {odmaUrl?.Value != null && (
                    <PreviewButton
                      url={odmaUrl?.Value as string}
                      variant="cherryButton"
                    >
                      Open
                    </PreviewButton>
                  )}
                </Box>

                <Box>
                  {odmaUrl?.Value != null && (
                    <CopyText
                      text={odmaUrl?.Value as string}
                      variant="cherryButton"
                    >
                      Copy Url
                    </CopyText>
                  )}
                </Box>

                <Box>
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>

              <HStack w="100%" pb={6}>
                <VStack alignItems="flex-start" w="100%">
                  <ViewControl
                    label="Enabled"
                    value={redemption?.IsEnabled ? "Yes" : "No"}
                  />

                  <ViewControl
                    label="Redirect URL"
                    value={redemption.RedirectUrl ?? "-"}
                  />

                  <ViewControl
                    label="Check Code"
                    value={redemption?.CheckCode ?? "-"}
                  />

                  <ViewControl
                    label="Venue ID"
                    value={redemption?.VenueId ?? "-"}
                  />

                  <ViewControl
                    label="Page Title"
                    value={redemption?.PageTitle ?? "-"}
                  />

                  <ViewControl label="Privacy Policy URL">
                    <Box flex={3}>
                      {(redemption?.PrivacyPolicyUrl && (
                        <PreviewButton
                          url={`${redemption?.PrivacyPolicyUrl}`}
                          adjustMargin
                        >
                          {redemption?.PrivacyPolicyUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>

                  <ViewControl label="Terms and Conditions URL">
                    <Box flex={3}>
                      {(redemption?.TermsConditionsUrl && (
                        <PreviewButton
                          url={`${redemption?.TermsConditionsUrl}`}
                          adjustMargin
                        >
                          {redemption?.TermsConditionsUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>

                  <ViewControl
                    label="Member Filter Query String"
                    value={redemption?.MemberFilterQuerystring ?? "-"}
                  />
                </VStack>

                {redemption?.LogoFileUrl && (
                  <Box alignSelf="flex-start">
                    <Image
                      maxHeight="200px"
                      objectFit="contain"
                      src={redemption?.LogoFileUrl}
                    />
                  </Box>
                )}
              </HStack>

              <Accordion
                allowMultiple
                allowToggle
                w="100%"
                defaultIndex={[0]}
                variant="section"
              >
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Form Header and Footer Content</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      {/* <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        Header Text
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2> */}
                      <AccordionPanel pb={4}>
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl label="Header Text">
                            <Box
                              flex={3}
                              fontSize="sm"
                              dangerouslySetInnerHTML={{
                                __html: redemption?.HeaderText ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Footer Text">
                            <Box
                              flex={3}
                              fontSize="sm"
                              dangerouslySetInnerHTML={{
                                __html: redemption?.FooterText ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Signee Instructions">
                            <Box
                              flex={3}
                              fontSize="sm"
                              dangerouslySetInnerHTML={{
                                __html: redemption?.SigneeInstructions ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Invitation SMS Message Body Format">
                            <Box
                              flex={3}
                              fontSize="sm"
                              dangerouslySetInnerHTML={{
                                __html:
                                  redemption?.InvitationSMSMessageBodyFormatOverride ??
                                  "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Error Message Suffix">
                            <Box
                              flex={3}
                              fontSize="sm"
                              dangerouslySetInnerHTML={{
                                __html: redemption?.ErrorMessageSuffix ?? "",
                              }}
                            />
                          </ViewControl>
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Theme Data</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <Box
                          width="100%"
                          borderWidth="1px"
                          borderRadius="lg"
                          boxShadow="lg"
                          overflowX="auto"
                          padding={6}
                          position="relative"
                          minHeight="90px"
                        >
                          <pre>
                            {JSON.stringify(redemption?.ThemeData, null, 2)}
                          </pre>
                        </Box>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
